import { getStrapiToken } from '@/services/auth/getStrapiToken';
/**
 * CA item+s Filter Options Hook
 *
 * Fetches dynamic filter options from the backend /filters endpoint.
 * Returns distinct values for each filter field that can be used to
 * populate filter dropdowns in the dashboard header.
 *
 * Usage:
 *   const { filterOptions, isLoading, error, refetch } = useFilterOptions(year);
 *   const scope3Options = filterOptions.scope3Category || [];
 */
// @/hooks/items/useFilterOptions.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/services/auth';

/**
 * Mapping from backend field names to frontend filter IDs
 * Backend returns camelCase keys, frontend uses filter IDs from DashboardHeader
 */
const BACKEND_TO_FRONTEND_MAP = {
  scope3Categories: 'scope3Category',
  orgLvl1: 'orgLevel1',
  orgLvl2: 'orgLevel2',
  orgLvl3: 'orgLevel3',
  orgLvl4: 'orgLevel4',
  orgLvl5: 'orgLevel5',
  orgGeo1: 'geoLevel1',
  orgGeo2: 'geoLevel2',
  orgGeo3: 'geoLevel3',
  productLvl1: 'productLevel1',
  productLvl2: 'productLevel2',
  productLvl3: 'productLevel3',
  productLvl4: 'productLevel4',
  productLvl5: 'productLevel5',
  supplierClass1: 'supplierClass1',
  supplierClass2: 'supplierClass2',
  supplierClass3: 'supplierClass3',
  supplierCountries: 'supplierCountry',
};

/**
 * Mapping from frontend filter IDs to backend query param names
 * Used when sending filter requests to the API
 */
export const FRONTEND_TO_BACKEND_MAP = {
  scope3Category: 'scope3Category',
  orgLevel1: 'orgLvl1',
  orgLevel2: 'orgLvl2',
  orgLevel3: 'orgLvl3',
  orgLevel4: 'orgLvl4',
  orgLevel5: 'orgLvl5',
  geoLevel1: 'orgGeo1',
  geoLevel2: 'orgGeo2',
  geoLevel3: 'orgGeo3',
  productLevel1: 'productLvl1',
  productLevel2: 'productLvl2',
  productLevel3: 'productLvl3',
  productLevel4: 'productLvl4',
  productLevel5: 'productLvl5',
  supplierClass1: 'supplierClass1',
  supplierClass2: 'supplierClass2',
  supplierClass3: 'supplierClass3',
  supplierCountry: 'supplierCountry',
};

/**
 * Convert frontend filter object to backend query params
 * @param {Object} filters - Frontend filter object { orgLevel1: 'value', ... }
 * @returns {Object} - Backend query params { orgLvl1: 'value', ... }
 */
export const convertFiltersToBackendParams = (filters) => {
  const backendParams = {};
  Object.entries(filters).forEach(([frontendKey, value]) => {
    if (value && (Array.isArray(value) ? value.length > 0 : true)) {
      const backendKey = FRONTEND_TO_BACKEND_MAP[frontendKey] || frontendKey;
      backendParams[backendKey] = value;
    }
  });
  return backendParams;
};

// Cache for filter options (keyed by projectId-year)
const filterOptionsCache = new Map();

/**
 * Clear the global filter options cache
 * Export this so other modules can clear the cache when data is known to change
 */
export const clearFilterOptionsCache = () => {
  filterOptionsCache.clear();
};

/**
 * Fetch filter options from the backend
 * Returns ALL available options regardless of active filters (not context-sensitive)
 * This allows users to freely combine any filters
 * @param {string|number} projectId
 * @param {string|number} year
 */
const fetchFilterOptions = async (projectId, year) => {
  const url = `${process.env.GATSBY_STRAPI_URL}/api/ca-items-row/project/${projectId}/year/${year}/filters`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getStrapiToken()}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch filter options: ${response.status}`);
  }

  return response.json();
};

/**
 * Transform backend response to frontend format
 */
const transformFilterOptions = (backendData) => {
  const transformed = {};

  Object.entries(backendData).forEach(([backendKey, values]) => {
    const frontendKey = BACKEND_TO_FRONTEND_MAP[backendKey];
    if (frontendKey && Array.isArray(values)) {
      transformed[frontendKey] = values;
    }
  });

  return transformed;
};

/**
 * React hook for fetching CA item+s filter options
 *
 * @param {string|number} year - The year to fetch filter options for
 * @returns {Object} Filter options state and methods
 */
export function useFilterOptions(year) {
  const [filterOptions, setFilterOptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  // Fetch filter options
  const fetchOptions = useCallback(async (forceRefresh = false) => {
    try {
      const projectId = authService.getProjectId();

      if (!projectId || !year) {
        setFilterOptions({});
        setIsLoading(false);
        return;
      }

      // Cache key is just projectId-year (no active filter context)
      const cacheKey = `${projectId}-${year}`;

      // Check cache (unless force refresh)
      if (!forceRefresh && filterOptionsCache.has(cacheKey)) {
        setFilterOptions(filterOptionsCache.get(cacheKey));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const backendData = await fetchFilterOptions(projectId, year);
      const transformed = transformFilterOptions(backendData);

      // Update cache
      filterOptionsCache.set(cacheKey, transformed);

      setFilterOptions(transformed);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching filter options:', err);
      }
      setError(err.message);
      setFilterOptions({});
    } finally {
      setIsLoading(false);
    }
  }, [year]);

  // Fetch on mount and when year changes
  useEffect(() => {
    if (year) {
      // Fetch all available options when year changes
      fetchOptions(true);
    }
  }, [year, fetchOptions]);

  /**
   * Force refresh filter options from API
   */
  const refetch = useCallback(() => {
    fetchedRef.current = true;
    return fetchOptions(true);
  }, [fetchOptions]);

  /**
   * Clear filter options cache
   */
  const clearCache = useCallback(() => {
    filterOptionsCache.clear();
  }, []);

  return {
    // Filter options keyed by filter ID
    filterOptions,

    // Loading/error state
    isLoading,
    error,

    // Methods
    refetch,
    clearCache,
  };
}
